import { useEffect, useRef } from "react";

import type { HelpScreen } from "../help/screens";
import { HelpButton } from "./HelpButton";

/**
 * A dialog that interrupts.
 *
 * ## Why this exists rather than another inline prompt
 *
 * Every prompt on the task page used to render at the BOTTOM of the stage
 * panel — below the instructions, a long way from the button that opened it.
 * Jeremy, 10 Oct 2026: *"when you hit the block button the reason box pops up
 * not just appears at the bottom, it is not intuitive to have it there."*
 *
 * He is right, and the reason is about what the thing IS. A block prompt is
 * not more content on the page; it is a question that has to be answered
 * before anything else happens. Rendering it as another section says the
 * opposite — it reads as optional, and on a stage with long instructions it
 * can open entirely below the fold, so pressing Block looks like it did
 * nothing at all.
 *
 * ## What it deliberately does NOT do
 *
 * **No focus trap.** A real one is a library or a hundred careful lines, and
 * it is the kind of thing that is worse half-built. These dialogs are two
 * controls and a text box; the field takes focus on open, Escape leaves, and
 * Tab eventually reaches the browser chrome. Worth revisiting if a dialog
 * here ever grows a form.
 *
 * **No scroll lock.** Locking the body is how you get a page that silently
 * loses its scroll position, and the backdrop already stops clicks.
 */
export function Modal({
  title,
  onCancel,
  children,
  wide = false,
  help,
}: {
  /** Names the dialog for a screen reader, and heads it on screen. */
  title: string;
  /** Escape, the backdrop and the close button all route here. */
  onCancel: () => void;
  children: React.ReactNode;
  /**
   * For READING and PLANNING dialogs -- every stage's instructions, the plan
   * grid (punch list #5, #6) -- which need the page's width, not a prompt's.
   */
  wide?: boolean;
  /**
   * The help screen(s) this pop-up's ? opens, in order of preference. The ? lives on the pop-up
   * rather than the screen behind it, because the backdrop covers that screen's ?.
   */
  help?: HelpScreen | readonly HelpScreen[];
}) {
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    // On `window`, not the card: focus may be in the textarea, on a button, or
    // nowhere at all, and Escape has to work in every one of those cases.
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  return (
    <div
      className="modal-backdrop"
      // The backdrop cancels, which is what people expect of a dialog they
      // have not committed to. `e.target === e.currentTarget` so a click that
      // STARTED inside the card and drifted out -- selecting text in the
      // textarea and releasing past its edge -- does not throw the answer
      // away.
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        className={wide ? "modal wide" : "modal"}
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="modal-head">
          <h3>{title}</h3>
          <span className="modal-head-tools">
            {help ? <HelpButton screens={help} /> : null}
            <button className="modal-close" onClick={onCancel} aria-label="Close">
              ✕
            </button>
          </span>
        </div>
        {children}
      </div>
    </div>
  );
}
